import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import withRouter from "components/Common/withRouter";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { DEFAULT_PAGE_SIZE, urlEndPoint } from "helpers/GeneralHelper";

import {
    Card,
    CardBody,
    Col,
    Container,
    Row,
} from "reactstrap";
import AchievementCustomTable from "components/Common/AchievementCustomTable";
import { submitTeamRecognitionMonthlyFilterData } from "model/TeamRecognitionMonthlyModel";
import { toast } from "react-toastify";

/** API may return `id`, `_id`, `Id`, or month header keys (match monthly list + details routes). */
const getMonthlyRowId = (item = {}) => {
    const raw =
        item?.id ??
        item?.Id ??
        item?._id ??
        item?.teamRecognitionMonthId ??
        item?.TeamRecognitionMonthId ??
        item?.recognitionMonthId ??
        item?.monthHeaderId;
    if (raw == null) return "";
    const s = String(raw).trim();
    return s || "";
};

/**
 * Same extraction idea as `TeamRecognitionMonthyList` / incentive achievements:
 * tolerate `records`, `data`, `Items`, or top-level array payloads.
 */
const normalizeRecords = (response) => {
    if (response == null) return [];
    const responseData =
        response?.data !== undefined && response?.data !== null ? response.data : response;
    const listData =
        responseData?.records ??
        responseData?.Records ??
        responseData?.data ??
        responseData?.items ??
        responseData?.Items ??
        responseData;
    if (!Array.isArray(listData)) return [];
    return listData;
};

const mapRowsWithStableId = (records) =>
    (Array.isArray(records) ? records : [])
        .map((item) => {
            const rid = getMonthlyRowId(item);
            return { ...item, id: rid || item?.id };
        })
        .filter((row) => String(getMonthlyRowId(row) || row?.id || "").trim() !== "");

const parseMonthNumber = (v) => {
    if (v == null || v === "") return NaN;
    const n = Number(v);
    if (Number.isFinite(n) && n >= 1 && n <= 12) return n;
    return NaN;
};

/**
 * True if `now` falls in [from, to] (inclusive). Accepts ISO or common date strings.
 */
const isNowInRange = (item, now) => {
    const from = dayjs(item?.teamRecognitionMonthFrom).startOf("day");
    const to = dayjs(item?.teamRecognitionMonthTo).endOf("day");
    if (!from.isValid() || !to.isValid()) return false;
    const t = now.valueOf();
    return t >= from.valueOf() && t <= to.valueOf();
};

const matchesCalendarMonth = (item, now) => {
    const y = Number(item?.recognitionYear ?? item?.year ?? item?.yearLabel);
    const m = parseMonthNumber(
        item?.recognitionMonth ?? item?.month ?? item?.monthLabel ?? item?.monthNumber
    );
    if (Number.isFinite(y) && Number.isFinite(m)) {
        return y === now.year() && m === now.month() + 1;
    }
    return false;
};

const pickCurrentMonthRecord = (records, now = dayjs()) => {
    if (!Array.isArray(records) || !records.length) return undefined;

    const withId = mapRowsWithStableId(records);
    if (!withId.length) return undefined;

    /* Same idea as incentive achievements: `monthDate` in current month */
    const byMonthDate = withId.find((item) => {
        if (!item?.monthDate) return false;
        const d = dayjs(item.monthDate);
        return d.isValid() && d.isSame(now, "month") && d.isSame(now, "year");
    });
    if (byMonthDate) return byMonthDate;

    const byCalendar = withId.find((item) => matchesCalendarMonth(item, now));
    if (byCalendar) return byCalendar;

    const byRange = withId.find((item) => isNowInRange(item, now));
    if (byRange) return byRange;

    return undefined;
};

/** When no row matches “today”, open the most recently ended / latest period so the user still lands on details. */
const pickLatestByEndDate = (records) => {
    const withId = mapRowsWithStableId(records);
    if (!withId.length) return undefined;

    return [...withId].sort((a, b) => {
        const tb = dayjs(b?.teamRecognitionMonthTo).valueOf();
        const ta = dayjs(a?.teamRecognitionMonthTo).valueOf();
        const vb = Number.isFinite(tb) ? tb : 0;
        const va = Number.isFinite(ta) ? ta : 0;
        return vb - va;
    })[0];
};

/** Prefer latest `monthDate` when range fields missing (parity with incentive list rows). */
const pickLatestByMonthDate = (records) => {
    const withId = mapRowsWithStableId(records);
    if (!withId.length) return undefined;
    const dated = withId.filter((r) => r?.monthDate && dayjs(r.monthDate).isValid());
    if (!dated.length) return undefined;
    return [...dated].sort((a, b) => dayjs(b.monthDate).valueOf() - dayjs(a.monthDate).valueOf())[0];
};

const TeamRecognitionAchievements = () => {
    const navigate = useNavigate();
    const [searchQuery, setSearchQuery] = useState("");
    const [achievementData, setAchievementData] = useState([]);
    const columns = [
        { Header: "Client", accessor: "client" },
        { Header: "Target", accessor: "target" },
        { Header: "Rs/Per", accessor: "rs" },
        { Header: "Negative", accessor: "negative" },
        { Header: "Achieve", accessor: "achieve" },
        { Header: "WorkDays", accessor: "workdays" },
        { Header: "Count", accessor: "count" },
        { Header: "Incentive", accessor: "incentive" },
    ];

    const filteredData = useMemo(() => {
        if (!searchQuery.trim()) return achievementData;

        return achievementData.filter((team) =>
            team.members.some((member) =>
                member.rows.some((row) =>
                    Object.values(row).some((val) =>
                        String(val).toLowerCase().includes(searchQuery.toLowerCase())
                    )
                )
            )
        );
    }, [searchQuery, achievementData]);

    const redirectToCurrentMonthDetails = async () => {
        try {
            const response = await submitTeamRecognitionMonthlyFilterData({
                pageNumber: 1,
                pageSize: DEFAULT_PAGE_SIZE,
            });

            const failed =
                response?.success === false ||
                response?.Success === false ||
                response?.isSuccess === false;
            if (failed && response?.message) {
                toast.error(response.message);
            }

            const records = normalizeRecords(response);

            let target =
                pickCurrentMonthRecord(records) ||
                pickLatestByEndDate(records) ||
                pickLatestByMonthDate(records) ||
                mapRowsWithStableId(records)[0];

            const rowId = getMonthlyRowId(target);
            if (rowId) {
                navigate(`/${urlEndPoint.teamRecognitionMonthlyDetails}/${encodeURIComponent(rowId)}`);
                return;
            }

            toast.info("No team recognition period found. Open the monthly list to choose one.");
            navigate(`/${urlEndPoint.teamRecognitionMonthlyList}`);
        } catch (e) {
            toast.error(e?.message || "Unable to load team recognition periods");
            navigate(`/${urlEndPoint.teamRecognitionMonthlyList}`);
        }
    };

    useEffect(() => {
        redirectToCurrentMonthDetails();
    }, []);

    return (
        <React.Fragment>
            <div className="page-content">
                <Container fluid>
                    <Card>
                        <CardBody>
                            <Row className="align-items-center g-2">
                                <Col xs="12" md="12" className="text-md-start text-center">
                                    <h5 className="fw-bold mb-0">Team recognition</h5>
                                    <hr />
                                </Col>
                            </Row>
                            <div className="mt-2">
                                {/* <AchievementCustomTable
                                    columns={columns}
                                    data={filteredData}
                                    isActionRequired={false}
                                /> */}
                            </div>
                        </CardBody>
                    </Card>
                </Container>
            </div>
        </React.Fragment>
    );
};

TeamRecognitionAchievements.propTypes = {
    orders: PropTypes.array,
    onGetOrders: PropTypes.func,
};

export default withRouter(TeamRecognitionAchievements);
